import { selectors } from 'modules/common';
import { useSelector } from 'modules/react-redux';
import { useGetRecognizedOcrAttachment } from 'pages/requestForm/hooks/useGetRecognizedOcrAttachment';
import { useOcrData } from 'pages/requestForm/hooks/useOcrData';
import { requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import { getRequestEditMode } from 'pages/requestForm/selectors/requestSelectors';
import { getRuleTypeByEditMode } from 'pages/requestForm/utils/helpers';
import { useEffect, useMemo, useState } from 'react';
import { useGetNetSuiteExpenseCategories } from 'shared/data/webApp/v1';
import { getSelectorSuggestedItems, isOcrEnabledForTemplate } from 'shared/helpers';

import { mapCategories } from './CategoryCell.helpers';
import { CategoryWithSubName } from './CategoryCell.types';

const useOcrCategories = (externalCategories: CategoryWithSubName[]) => {
    const request = useSelector(requestSelector);
    const company = useSelector((state) => selectors.company.getCompanyById(state, request.companyId));
    const ocrAttachment = useGetRecognizedOcrAttachment(request);
    const shouldUseOcr = ocrAttachment && isOcrEnabledForTemplate(company, request.integrationCode);

    const {
        data: ocrData,
        refetch: refetchOcrData,
        isFetching: isOcrFetching,
    } = useOcrData(request, ocrAttachment?.id);

    useEffect(() => {
        if (shouldUseOcr && !ocrData) refetchOcrData();
    }, [shouldUseOcr, ocrData, refetchOcrData]);

    const categories = useMemo(() => {
        if (!shouldUseOcr || !ocrData) return externalCategories;

        const vendorIds = ocrData.document?.vendorIds || [];

        return getSelectorSuggestedItems(externalCategories, vendorIds);
    }, [externalCategories, shouldUseOcr, ocrData]);

    return { categories, isOcrFetching };
};

export const useCategories = (query?: string) => {
    const request = useSelector(requestSelector);
    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const [currentCategories, setCurrentCategories] = useState<CategoryWithSubName[]>([]);
    const ruleType = getRuleTypeByEditMode(editMode);

    const { data: netSuiteExpenseCategories, isFetching: isFetchingNetSuiteExpenseCategories } =
        useGetNetSuiteExpenseCategories(
            {
                query: {
                    companyId: request.companyId,
                    requestId: request.id,
                    ruleType,
                    query,
                    rowNum: 116,
                    startFrom: 0,
                },
            },
            {
                staleTime: 1000 * 60 * 5,
            }
        );

    useEffect(() => {
        if (!netSuiteExpenseCategories?.values) return;

        setCurrentCategories(mapCategories(netSuiteExpenseCategories.values));
    }, [netSuiteExpenseCategories]);

    const { categories, isOcrFetching } = useOcrCategories(currentCategories);

    return {
        categories,
        isLoading: isFetchingNetSuiteExpenseCategories || isOcrFetching,
    };
};
